// We Site Custom JavaScript
// Handles product card variant switching and metaobject colors globally

document.addEventListener('DOMContentLoaded', function() {
  initializeProductCardVariants();
  initializeHorizontalProductCardVariants();
  applyMetaobjectColors();
  initializeTabDragScrolling();
  initializeDiscountPercentageUpdates();
  initializeProductInfoStickyTop();
  initializeProductInfoScrollChain();
  initializeVariantImageUpdates();
  initializeCarouselButtonStates();
  initializePrivacyConsentForms();
  initializeLocalizationForms();
  patchHeaderSidebarLocalization();
  patchLocalizationPopover();
  
  // Watch for dynamic content changes (like related products)
  const observer = new MutationObserver(function(mutations) {
    initializeProductCardVariants();
    initializeHorizontalProductCardVariants();
    applyMetaobjectColors();
    initializeTabDragScrolling();
    initializeDiscountPercentageUpdates();
    initializeProductInfoStickyTop();
    initializeProductInfoScrollChain();
    initializeVariantImageUpdates();
    initializeCarouselButtonStates();
    initializePrivacyConsentForms();
    initializeLocalizationForms();
    patchHeaderSidebarLocalization();
    patchLocalizationPopover();
  });
  observer.observe(document.body, { childList: true, subtree: true });
});

function initializeLocalizationForms() {
  document.querySelectorAll('form[id^="localization-form"]').forEach((form) => {
    if (form.dataset.localizationInitialized) return;
    form.dataset.localizationInitialized = 'true';

    let isSubmitting = false;

    const submitSelection = (option) => {
      if (isSubmitting) return;
      isSubmitting = true;

      const localeInput = form.querySelector('input[name="locale_code"]');
      const countryInput = form.querySelector('input[name="country_code"]');

      if (option.dataset.localeCode && localeInput) {
        localeInput.value = option.dataset.localeCode;
      }

      if (option.dataset.countryCode && countryInput) {
        countryInput.value = option.dataset.countryCode;
      }

      const returnToInput = form.querySelector('input[name="return_to"]');
      if (returnToInput) {
        returnToInput.value = `${window.location.pathname}${window.location.search}`;
      }

      form.submit();
    };

    const handleSelection = (event) => {
      const option = event.target.closest('[data-locale-code], [data-country-code]');
      if (!option || !form.contains(option)) return;

      event.preventDefault();
      event.stopPropagation();
      submitSelection(option);
    };

    form.addEventListener('click', handleSelection, true);
  });
}

function isLocalizationPopoverTarget(target) {
  return Boolean(target?.closest?.('x-popover[id^="popover-localization"]'));
}

function patchHeaderSidebarLocalization() {
  customElements.whenDefined('header-sidebar').then(() => {
    const Sidebar = customElements.get('header-sidebar');
    if (!Sidebar || Sidebar.prototype.__localizationPatched) return;

    const dialogPrototype = Object.getPrototypeOf(Sidebar.prototype);

    Sidebar.prototype.hideForOutsideClickTarget = function(target) {
      if (isLocalizationPopoverTarget(target)) {
        return false;
      }

      if (this.contains(target)) {
        return false;
      }

      if (this.shadowRoot?.contains(target)) {
        return target === this.getShadowPartByName('overlay');
      }

      return true;
    };

    Sidebar.prototype.allowOutsideClickForTarget = function(target) {
      if (isLocalizationPopoverTarget(target)) {
        return true;
      }

      return dialogPrototype.allowOutsideClickForTarget
        ? dialogPrototype.allowOutsideClickForTarget.call(this, target)
        : false;
    };

    Sidebar.prototype.__localizationPatched = true;
  });
}

function patchLocalizationPopover() {
  customElements.whenDefined('x-popover').then(() => {
    const Popover = customElements.get('x-popover');
    if (!Popover || Popover.prototype.__localizationPatched) return;

    const originalHideForOutsideClickTarget = Popover.prototype.hideForOutsideClickTarget;

    Popover.prototype.hideForOutsideClickTarget = function(target) {
      if (this.id?.startsWith('popover-localization') && target?.closest?.('[data-locale-code], [data-country-code]')) {
        return false;
      }

      return originalHideForOutsideClickTarget.call(this, target);
    };

    Popover.prototype.__localizationPatched = true;
  });
}

function initializeProductCardVariants() {
  const productCards = document.querySelectorAll('.product-card--new-design');
  
  productCards.forEach(card => {
    // Skip if already initialized
    if (card.dataset.variantsInitialized) return;
    card.dataset.variantsInitialized = 'true';
    
    const variantOptions = card.querySelectorAll('.product-card__variant-option');
    
    variantOptions.forEach(option => {
      option.addEventListener('click', function() {
        const card = this.closest('.product-card--new-design');
        const allOptions = card.querySelectorAll('.product-card__variant-option');
        
        // Remove selected class from all options
        allOptions.forEach(opt => opt.classList.remove('is-selected'));
        
        // Add selected class to clicked option
        this.classList.add('is-selected');
        
        // Get variant data
        const variantId = this.dataset.variantId;
        const variantUrl = this.dataset.variantUrl;
        const variantPrice = this.dataset.variantPrice;
        const variantCompareAtPrice = this.dataset.variantCompareAtPrice || '';
        const variantPriceNumeric = parseInt(this.dataset.variantPriceNumeric) || 0;
        const variantCompareAtPriceNumeric = parseInt(this.dataset.variantCompareAtPriceNumeric) || 0;
        const variantImage = this.dataset.variantImage;
        
        // Update product card with selected variant
        updateProductCardVariant(card, variantId, variantUrl, variantPrice, variantCompareAtPrice, variantPriceNumeric, variantCompareAtPriceNumeric, variantImage);
      });
    });
  });
}

function updateProductCardVariant(card, variantId, variantUrl, variantPrice, variantCompareAtPrice, variantPriceNumeric, variantCompareAtPriceNumeric, variantImage) {
  // Update product image if variant has different image
  if (variantImage) {
    const productImage = card.querySelector('.product-card__image--primary');
    if (productImage) {
      productImage.removeAttribute('srcset');
      productImage.removeAttribute('sizes');
      productImage.src = variantImage;
    }
  }

  const priceList = card.querySelector('.price-list') || card.querySelector('.product-card__prices');
  // Keep shop-wide "ab …" pricing when variants have different prices
  const keepFromPrice = priceList?.hasAttribute('data-from-price');

  if (!keepFromPrice) {
    // Update price if variant has different price
    if (variantPrice) {
      const priceSelectors = [
        '.price-list sale-price',
        '.product-card__prices sale-price',
        '.price-list .price',
        '.price-list .price--sale',
        '.price-list .price--regular'
      ];
      
      for (const selector of priceSelectors) {
        const priceElements = card.querySelectorAll(selector);
        if (priceElements.length > 0) {
          priceElements.forEach(element => {
            element.textContent = variantPrice;
          });
          break;
        }
      }
    }
    
    // Update compare-at-price if variant has compare_at_price
    if (priceList) {
      if (variantCompareAtPrice && variantCompareAtPriceNumeric > variantPriceNumeric) {
        // Update or create compare-at-price element
        let compareAtPriceEl = priceList.querySelector('compare-at-price');
        if (!compareAtPriceEl) {
          const salePriceEl = priceList.querySelector('sale-price');
          if (salePriceEl) {
            compareAtPriceEl = document.createElement('compare-at-price');
            compareAtPriceEl.className = 'text-subdued line-through';
            salePriceEl.parentNode.insertBefore(compareAtPriceEl, salePriceEl.nextSibling);
          }
        }
        if (compareAtPriceEl) {
          compareAtPriceEl.textContent = variantCompareAtPrice;
        }
        
        // Calculate and update discount percentage
        const discountAmount = variantCompareAtPriceNumeric - variantPriceNumeric;
        const discountPercentage = Math.round((discountAmount * 100) / variantCompareAtPriceNumeric);
        
        let discountEl = priceList.querySelector('discount-percentage');
        if (!discountEl && compareAtPriceEl) {
          discountEl = document.createElement('discount-percentage');
          discountEl.className = 'discount-percentage';
          compareAtPriceEl.parentNode.insertBefore(discountEl, compareAtPriceEl.nextSibling);
        }
        if (discountEl) {
          discountEl.textContent = `-${discountPercentage}%`;
        }
      } else {
        // Remove compare-at-price and discount-percentage if not on sale
        const compareAtPriceEl = priceList.querySelector('compare-at-price');
        if (compareAtPriceEl) {
          compareAtPriceEl.remove();
        }
        const discountEl = priceList.querySelector('discount-percentage');
        if (discountEl) {
          discountEl.remove();
        }
      }
    }
  }
  
  // Update product link to point to specific variant
  if (variantUrl) {
    const productLink = card.querySelector('.product-card__media');
    if (productLink) {
      productLink.href = variantUrl;
    }
  }
}

// Horizontal complementary card (custom) variant switching
function initializeHorizontalProductCardVariants() {
  const horizontalCards = document.querySelectorAll('.horizontal-product-card');
  
  horizontalCards.forEach(card => {
    if (card.dataset.horizontalVariantsInitialized) return;
    card.dataset.horizontalVariantsInitialized = 'true';

    const variantOptions = card.querySelectorAll('.horizontal-product-card__variant-option');
    
    variantOptions.forEach(option => {
      option.addEventListener('click', function() {
        const card = this.closest('.horizontal-product-card');
        const allOptions = card.querySelectorAll('.horizontal-product-card__variant-option');
        
        // Toggle selection ring
        allOptions.forEach(opt => opt.classList.remove('is-selected'));
        this.classList.add('is-selected');

        const variantId = this.dataset.variantId;
        const variantUrl = this.dataset.variantUrl;
        const variantPrice = this.dataset.variantPrice;
        const variantCompareAtPrice = this.dataset.variantCompareAtPrice || '';
        const variantPriceNumeric = parseInt(this.dataset.variantPriceNumeric) || 0;
        const variantCompareAtPriceNumeric = parseInt(this.dataset.variantCompareAtPriceNumeric) || 0;
        const variantImage = this.dataset.variantImage;

        updateHorizontalProductCardVariant(card, variantId, variantUrl, variantPrice, variantCompareAtPrice, variantPriceNumeric, variantCompareAtPriceNumeric, variantImage);
      });
    });
  });
}

function updateHorizontalProductCardVariant(card, variantId, variantUrl, variantPrice, variantCompareAtPrice, variantPriceNumeric, variantCompareAtPriceNumeric, variantImage) {
  // Image
  if (variantImage) {
    const img = card.querySelector('.horizontal-product-card__image');
    if (img) img.src = variantImage;
  }

  const priceList = card.querySelector('.price-list');
  // Keep shop-wide "ab …" pricing when variants have different prices
  const keepFromPrice = priceList?.hasAttribute('data-from-price');

  if (!keepFromPrice) {
    // Prices
    if (variantPrice) {
      const priceSelectors = [
        '.price-list sale-price',
        '.price-list .price',
        '.price-list .price--sale',
        '.price-list .price--regular'
      ];
      for (const selector of priceSelectors) {
        const els = card.querySelectorAll(selector);
        if (els.length) {
          els.forEach(el => { el.textContent = variantPrice; });
          break;
        }
      }
    }

    // Update compare-at-price and discount percentage
    if (priceList) {
      if (variantCompareAtPrice && variantCompareAtPriceNumeric > variantPriceNumeric) {
        let compareAtPriceEl = priceList.querySelector('compare-at-price');
        if (!compareAtPriceEl) {
          const salePriceEl = priceList.querySelector('sale-price');
          if (salePriceEl) {
            compareAtPriceEl = document.createElement('compare-at-price');
            compareAtPriceEl.className = 'text-subdued line-through';
            salePriceEl.parentNode.insertBefore(compareAtPriceEl, salePriceEl.nextSibling);
          }
        }
        if (compareAtPriceEl) {
          compareAtPriceEl.textContent = variantCompareAtPrice;
        }

        const discountAmount = variantCompareAtPriceNumeric - variantPriceNumeric;
        const discountPercentage = Math.round((discountAmount * 100) / variantCompareAtPriceNumeric);

        let discountEl = priceList.querySelector('discount-percentage');
        if (!discountEl && compareAtPriceEl) {
          discountEl = document.createElement('discount-percentage');
          discountEl.className = 'discount-percentage';
          compareAtPriceEl.parentNode.insertBefore(discountEl, compareAtPriceEl.nextSibling);
        }
        if (discountEl) {
          discountEl.textContent = `-${discountPercentage}%`;
        }
      } else {
        const compareAtPriceEl = priceList.querySelector('compare-at-price');
        if (compareAtPriceEl) {
          compareAtPriceEl.remove();
        }
        const discountEl = priceList.querySelector('discount-percentage');
        if (discountEl) {
          discountEl.remove();
        }
      }
    }
  }

  // Links (both image and title links inside the card)
  if (variantUrl) {
    const links = card.querySelectorAll('a[href*="/products/"]');
    links.forEach(link => { link.href = variantUrl; });
  }
}

function applyMetaobjectColors() {
  // Handle product page variant picker swatches
  const variantSwatches = document.querySelectorAll('.variant-picker .custom-color-swatch, .variant-picker .block-swatch__color');
  variantSwatches.forEach(swatch => {
    const metaobjectColor = swatch.getAttribute('data-metaobject-color');
    
    if (metaobjectColor) {
      swatch.style.setProperty('--swatch-background', `linear-gradient(to right, ${metaobjectColor}, ${metaobjectColor})`);
    }
  });
}

function initializeTabDragScrolling() {
  const tabContainers = document.querySelectorAll('.content-tabs');
  
  tabContainers.forEach(container => {
    // Skip if already initialized
    if (container.dataset.dragScrollingInitialized) return;
    container.dataset.dragScrollingInitialized = 'true';
    
    // Access the shadow root to get the tab-list part
    const shadowRoot = container.shadowRoot;
    if (!shadowRoot) return;
    
    const tabList = shadowRoot.querySelector('[part="tab-list"]');
    if (!tabList) return;
    
    let isDown = false;
    let startX;
    let scrollLeft;
    
    // Mouse events
    tabList.addEventListener('mousedown', (e) => {
      isDown = true;
      tabList.style.cursor = 'grabbing';
      startX = e.pageX - tabList.offsetLeft;
      scrollLeft = tabList.scrollLeft;
      e.preventDefault();
    });
    
    tabList.addEventListener('mouseleave', () => {
      isDown = false;
      tabList.style.cursor = 'grab';
    });
    
    tabList.addEventListener('mouseup', () => {
      isDown = false;
      tabList.style.cursor = 'grab';
    });
    
    tabList.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - tabList.offsetLeft;
      const walk = (x - startX) * 2; // Scroll speed multiplier
      tabList.scrollLeft = scrollLeft - walk;
    });
    
    // Touch events for mobile
    let touchStartX;
    let touchScrollLeft;
    
    tabList.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].pageX - tabList.offsetLeft;
      touchScrollLeft = tabList.scrollLeft;
    }, { passive: true });
    
    tabList.addEventListener('touchmove', (e) => {
      if (!touchStartX) return;
      const x = e.touches[0].pageX - tabList.offsetLeft;
      const walk = (x - touchStartX) * 2;
      tabList.scrollLeft = touchScrollLeft - walk;
    }, { passive: true });
    
    tabList.addEventListener('touchend', () => {
      touchStartX = null;
    });
    
    // Set initial cursor style
    tabList.style.cursor = 'grab';
    tabList.style.userSelect = 'none';
  });
}

function initializeDiscountPercentageUpdates() {
  // Find all product forms and listen for variant changes
  const productForms = document.querySelectorAll('form[action*="/cart/add"]');
  
  productForms.forEach(form => {
    // Skip if already initialized
    if (form.dataset.discountPercentageInitialized) return;
    form.dataset.discountPercentageInitialized = 'true';
    
    // Listen for variant:change events
    form.addEventListener('variant:change', function(event) {
      updateDiscountPercentage(event.detail.variant);
    });
    
    // Also listen for product:rerender to update after re-render
    form.addEventListener('product:rerender', function() {
      // Wait a bit for the DOM to update
      setTimeout(() => {
        const priceList = form.closest('.product-info')?.querySelector('price-list');
        if (priceList) {
          const compareAtPriceEl = priceList.querySelector('compare-at-price');
          const salePriceEl = priceList.querySelector('sale-price');
          
          if (compareAtPriceEl && salePriceEl) {
            // Extract numeric values from the price text
            const compareAtPriceText = compareAtPriceEl.textContent.trim();
            const salePriceText = salePriceEl.textContent.trim();
            
            // Try to get prices from variant data if available
            const variantScript = form.querySelector('script[data-variant]');
            if (variantScript) {
              try {
                const variant = JSON.parse(variantScript.textContent);
                updateDiscountPercentage(variant);
                return;
              } catch (e) {
                // Fall through to text parsing
              }
            }
            
            // Parse prices from text (remove currency symbols and commas)
            const compareAtPrice = parsePrice(compareAtPriceText);
            const salePrice = parsePrice(salePriceText);
            
            if (compareAtPrice && salePrice && compareAtPrice > salePrice) {
              calculateAndUpdateDiscount(priceList, compareAtPrice, salePrice);
            } else {
              // Remove discount percentage if not on sale
              const discountEl = priceList.querySelector('discount-percentage');
              if (discountEl) {
                discountEl.remove();
              }
            }
          }
        }
      }, 100);
    });
  });
}

function updateDiscountPercentage(variant) {
  if (!variant || !variant.compare_at_price || !variant.price) return;
  
  // Find the price-list element near the form
  const forms = document.querySelectorAll('form[action*="/cart/add"]');
  forms.forEach(form => {
    const priceList = form.closest('.product-info')?.querySelector('price-list');
    if (priceList) {
      if (variant.compare_at_price > variant.price) {
        calculateAndUpdateDiscount(priceList, variant.compare_at_price, variant.price);
      } else {
        // Remove discount percentage if not on sale
        const discountEl = priceList.querySelector('discount-percentage');
        if (discountEl) {
          discountEl.remove();
        }
      }
    }
  });
}

function calculateAndUpdateDiscount(priceList, compareAtPrice, salePrice) {
  const discountAmount = compareAtPrice - salePrice;
  const discountPercentage = Math.round((discountAmount * 100) / compareAtPrice);
  
  // Find or create discount-percentage element
  let discountEl = priceList.querySelector('discount-percentage');
  const compareAtPriceEl = priceList.querySelector('compare-at-price');
  
  if (!discountEl && compareAtPriceEl) {
    // Create the element if it doesn't exist
    discountEl = document.createElement('discount-percentage');
    discountEl.className = 'discount-percentage';
    // Insert after compare-at-price
    compareAtPriceEl.parentNode.insertBefore(discountEl, compareAtPriceEl.nextSibling);
  }
  
  if (discountEl) {
    discountEl.textContent = `-${discountPercentage}%`;
  }
}

function parsePrice(priceText) {
  // Remove currency symbols, commas, and whitespace, then extract number
  const cleaned = priceText.replace(/[^\d.,]/g, '').replace(/,/g, '');
  const match = cleaned.match(/[\d.]+/);
  return match ? parseFloat(match[0]) : null;
}

function initializeVariantImageUpdates() {
  // Find all product forms
  const productForms = document.querySelectorAll('form[action*="/cart/add"]');
  
  productForms.forEach(form => {
    // Skip if already initialized
    if (form.dataset.variantImageUpdatesInitialized) return;
    form.dataset.variantImageUpdatesInitialized = 'true';
    
    // Listen for variant:change events
    form.addEventListener('variant:change', function(event) {
      const variant = event.detail.variant;
      if (!variant || !variant.id) return;
      
      const variantId = variant.id.toString();
      
      // Find all variant images in product-info
      const productInfo = form.closest('.product-info') || document.querySelector('.product-info');
      if (!productInfo) return;
      
      const variantImages = productInfo.querySelectorAll('.we-variant-image[data-variant-id]');
      
      variantImages.forEach(image => {
        if (image.getAttribute('data-variant-id') === variantId) {
          image.style.display = '';
        } else {
          image.style.display = 'none';
        }
      });
    });
  });
}

function initializeCarouselButtonStates() {
  // Find all carousel prev/next buttons
  const prevButtons = document.querySelectorAll('carousel-prev-button');
  const nextButtons = document.querySelectorAll('carousel-next-button');
  
  const updateButtonState = (button, isPrev) => {
    const carouselId = button.getAttribute('aria-controls');
    if (!carouselId) return;
    
    const carousel = document.getElementById(carouselId);
    if (!carousel) return;
    
    const innerButton = button.querySelector('button');
    if (!innerButton) return;
    
    // Only manage buttons for horizontally scrolling carousels.
    // Effect carousels (e.g. Shop the Look) stack slides — scrollWidth never exceeds
    // clientWidth, so disabling here would permanently break prev/next.
    if (carousel.scrollWidth <= carousel.clientWidth) {
      return;
    }
    
    // Check initial scroll position
    const scrollLeft = Math.round(Math.abs(carousel.scrollLeft));
    const scrollEdgeThreshold = 100;
    const isAtStart = scrollLeft < scrollEdgeThreshold;
    const isAtEnd = carousel.scrollWidth - carousel.clientWidth < scrollLeft + scrollEdgeThreshold;
    
    if (isPrev) {
      innerButton.disabled = isAtStart;
    } else {
      innerButton.disabled = isAtEnd;
    }
  };
  
  prevButtons.forEach(button => {
    if (button.dataset.stateInitialized) return;
    button.dataset.stateInitialized = 'true';
    
    // Check initial state after a short delay to ensure carousel is rendered
    setTimeout(() => updateButtonState(button, true), 100);
    
    // Also check when carousel scrolls
    const carouselId = button.getAttribute('aria-controls');
    if (carouselId) {
      const carousel = document.getElementById(carouselId);
      if (carousel) {
        carousel.addEventListener('scroll', () => updateButtonState(button, true), { passive: true });
      }
    }
  });
  
  nextButtons.forEach(button => {
    if (button.dataset.stateInitialized) return;
    button.dataset.stateInitialized = 'true';
    
    // Check initial state after a short delay to ensure carousel is rendered
    setTimeout(() => updateButtonState(button, false), 100);
    
    // Also check when carousel scrolls
    const carouselId = button.getAttribute('aria-controls');
    if (carouselId) {
      const carousel = document.getElementById(carouselId);
      if (carousel) {
        carousel.addEventListener('scroll', () => updateButtonState(button, false), { passive: true });
      }
    }
  });
}

function isValidEmailAddress(value) {
  const email = value.trim();
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email);
}

function getInvalidEmailMessage() {
  return window.themeVariables?.strings?.invalidEmail
    || 'Please enter a valid email address (e.g. name@example.com).';
}

function initializePrivacyConsentForms() {
  document.querySelectorAll('[data-privacy-consent-checkbox]').forEach((checkbox) => {
    if (checkbox.dataset.privacyConsentInitialized) return;
    checkbox.dataset.privacyConsentInitialized = 'true';

    const form = checkbox.closest('form');
    if (!form) return;

    const submitButtons = form.querySelectorAll('button[type="submit"]');
    if (!submitButtons.length) return;

    const emailInput = form.querySelector(
      'input[type="email"][name="contact[email]"], input[type="email"]'
    );

    const syncEmailValidity = () => {
      if (!emailInput) return;

      const value = emailInput.value.trim();
      if (value === '') {
        emailInput.setCustomValidity('');
      } else if (!isValidEmailAddress(value)) {
        emailInput.setCustomValidity(getInvalidEmailMessage());
      } else {
        emailInput.setCustomValidity('');
      }
    };

    const isFormReady = () => {
      const hasValidEmail = !emailInput || isValidEmailAddress(emailInput.value);
      return checkbox.checked && hasValidEmail;
    };

    const syncSubmitState = () => {
      syncEmailValidity();
      const canSubmit = isFormReady();
      submitButtons.forEach((button) => {
        const shouldDisable = !canSubmit;
        const hasDisabledClass = button.classList.contains('button--disabled');
        if (button.disabled === shouldDisable && hasDisabledClass === shouldDisable) return;
        button.disabled = shouldDisable;
        button.classList.toggle('button--disabled', shouldDisable);
        button.setAttribute('aria-disabled', canSubmit ? 'false' : 'true');
      });
    };

    checkbox.addEventListener('change', syncSubmitState);
    emailInput?.addEventListener('input', syncSubmitState);
    emailInput?.addEventListener('blur', syncSubmitState);
    emailInput?.addEventListener('change', syncSubmitState);

    form.addEventListener('submit', (event) => {
      syncEmailValidity();
      if (emailInput && !emailInput.checkValidity()) {
        event.preventDefault();
        emailInput.reportValidity();
      }
    });

    syncSubmitState();
  });
}

function initializeProductInfoStickyTop() {
  // Find all safe-sticky elements with product-info class
  const productInfoElements = document.querySelectorAll('safe-sticky.product-info');
  
  productInfoElements.forEach(element => {
    // Quick-buy modal: no header sticky offset; avoids broken layout when scrolling inside the dialog
    if (element.closest('.quick-buy-modal')) return;

    // Skip if already initialized
    if (element.dataset.stickyTopInitialized) return;
    element.dataset.stickyTopInitialized = 'true';

    // Main PDP uses page-scroll buy box (gallery is the sticky scroller)
    if (element.closest('.shopify-section--main-product')) {
      element.style.removeProperty('top');
      return;
    }
    
    // Function to update top value based on header visibility
    const updateTopValue = () => {
      // Get header height from CSS variable
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const padding = 20; // Additional padding
      
      // Check if header is visible using CSS variable or class
      const headerIsVisible = getComputedStyle(document.documentElement).getPropertyValue('--header-is-visible').trim() === '1';
      const header = document.querySelector('.header');
      const headerIsHidden = header && header.classList.contains('is-hidden');
      
      // Set top to 0 only when header is explicitly hidden, otherwise use header height + padding
      if (headerIsHidden) {
        element.style.top = '0px';
      } else if (headerHeight > 0) {
        element.style.top = `${headerHeight + padding}px`;
      } else {
        // If header height is not available yet, use padding as fallback
        // This handles initial load before header height is calculated
        element.style.top = `${padding}px`;
      }
    };
    
    // Use MutationObserver to watch for inline style changes to top
    const styleObserver = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          updateTopValue();
        }
      });
    });
    
    // Watch for header visibility changes
    const headerObserver = new MutationObserver(function() {
      updateTopValue();
    });
    
    // Observe style attribute changes on the element
    styleObserver.observe(element, {
      attributes: true,
      attributeFilter: ['style']
    });
    
    // Observe header class changes and CSS variable changes
    const header = document.querySelector('.header');
    if (header) {
      headerObserver.observe(header, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
    
    // Watch for CSS variable changes on document root
    const rootObserver = new MutationObserver(function() {
      updateTopValue();
    });
    rootObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style']
    });
    
    // Also listen to scroll events to update when header visibility changes
    let lastScrollY = window.scrollY;
    window.addEventListener('scroll', () => {
      // Only update if scroll direction changed significantly
      const currentScrollY = window.scrollY;
      if (Math.abs(currentScrollY - lastScrollY) > 50) {
        updateTopValue();
        lastScrollY = currentScrollY;
      }
    }, { passive: true });
    
    // Set initial value
    updateTopValue();
  });
}

/**
 * Desktop PDP: wheel over the gallery scrolls images first; at the
 * gallery edge, chain to the page. Buy box scrolls with the page only
 * (that column is where the PDP scroll ends).
 */
function initializeProductInfoScrollChain() {
  const desktopMq = window.matchMedia('(min-width: 1000px)');

  document.querySelectorAll('.shopify-section--main-product .product').forEach((product) => {
    if (product.dataset.scrollChainInitialized) return;
    product.dataset.scrollChainInitialized = 'true';

    const gallery = product.querySelector('.product-gallery');
    if (!gallery) return;

    const atBottom = () => gallery.scrollTop + gallery.clientHeight >= gallery.scrollHeight - 1;
    const atTop = () => gallery.scrollTop <= 1;
    const hasOverflow = () => gallery.scrollHeight - gallery.clientHeight > 1;

    gallery.addEventListener(
      'wheel',
      (event) => {
        if (!desktopMq.matches || event.ctrlKey) return;

        const deltaY = event.deltaY;
        if (!deltaY || !hasOverflow()) return;

        // Still room inside the gallery → let it scroll natively
        if ((deltaY > 0 && !atBottom()) || (deltaY < 0 && !atTop())) {
          return;
        }

        // Hit the gallery edge → continue on the page
        window.scrollBy(0, deltaY);
        event.preventDefault();
      },
      { passive: false }
    );
  });
}
